import React, {
  useState,
  ChangeEvent,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from "react";
import {
  Upload,
  Button,
  Table,
  message,
  Input,
  DatePicker,
  Typography,
  Radio,
  Select,
  Modal,
  Checkbox,
} from "antd";
import type { UploadProps } from "antd/es/upload/interface";
import dayjs, { Dayjs } from "dayjs";
import {
  UploadOutlined,
  CloseOutlined,
  CalendarOutlined,
  DollarOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import Papa from "papaparse";
import { GeoLiftJobConfigService } from "../../../../services/jobConfigServiceFactory/GeoLiftJobConfigService";
import {
  summarizeMarkets,
  STATE_NAME_TO_POSTAL,
} from "../../../../utils/utils";
import { fuzzyMatchLocation } from "../../../../utils/locationMatching";
import WorldUSSalesMap from "../../../../components/WorldHoldoutTreatmentMap";
import ColumnMappingModal from "../../../../components/ColumnMappingModal";
import DatabaseDataSelector from "../../../../components/DatabaseDataSelector";
import {
  validateGeoLiftData,
  convertMappingsToBackendFormat,
  type ColumnMapping,
  type ValidationResult,
} from "../../../../utils/dataValidation";
import axiosInstance from "../../../../utils/axiosInstance";
import { GEO_LIFT_GET_TEST_MARKET } from "../../../../constants/API";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { useSearchParams, useParams } from "react-router-dom";
import { DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD } from "../../../../constants/common";
import CustomExcelIcon from "../../../../assets/icons/CustomExcelIcon";

const { Text } = Typography;
export interface JobConfig {
  tenantId: string;
  jobConfigId: string;
  status: string;
  name: string;
  jobType: string;
  is_ai_qc_enabled: boolean;
  description: string | null;
  dataSourceConfigId: string | null;

  dataTransformationConfig: Record<string, unknown>;

  dataMappingConfig: {
    mappings: {
      geoLift: {
        columnMapping: {
          location_variables: string[];
          date_variables: string[];
          kpi_variables: string[];
        };
      };
    };
  };

  modelVersion: string;

  modelParameterConfig: {
    parameters: {
      buyType: string;
      scmType: string;
      isSales: boolean;
      dateFormat: string;
      durationType: string;
      includeMarket: string[];
      campaignStartDate: string;
      bauSpent: number;
      statSigThreshold: number;
    };
  };

  modelOutputConfig: {
    outputData: {
      aggregateLift: string;
      outputWithoutCampaign: number;
      totalAdSpend: number;
      outputWithCampaign: number;
    };
  };

  createdDate: string;
  modifiedDate: string;
  version: number;
}

interface PreCampaignProps {
  jobConfigId: string;
  fileData?: string;
  onValuesChange?: (values: {
    geoLiftStudyName: string;
    buyType: "national" | "non-national";
    fileList: File[];
    columnMappings?: { [key: string]: string | null };
    campaignStartDate?: string;
    is_ai_qc_enabled: boolean;
    bauSpent: number;
    statSigThreshold: number;
    isSales: boolean | undefined;
  }) => void;
  initialValues?: {
    geoLiftStudyName: string;
    buyType: "national" | "non-national";
    fileList: File[];
    columnMappings?: { [key: string]: string | null };
    campaignStartDate?: string;
    is_ai_qc_enabled: boolean;
    bauSpent: number;
    statSigThreshold: number;
    isSales: boolean | undefined;
  };
  setCampaignDate: (value: Dayjs | null) => void;
  setFileUploaded?: React.Dispatch<React.SetStateAction<boolean>>;
  mode: any;
  selectedMarketsIncludes: any[];
  setScmType: (e: string) => void;
  setSelectedMarketsIncludes: (e: string[]) => void;
  scmType: string;
  isSales: boolean;
  setIsSales: (e: boolean) => void;
  viewMode?: boolean;
}

export interface PreCampaignRef {
  getData: () => {
    geoLiftStudyName: string;
    buyType: "national" | "non-national";
    fileList: File[];
    campaignStartDate?: string;
    bauSpent: number;
    statSigThreshold: number;
    isSales: boolean;
  };
}

const PreCampaign = forwardRef<PreCampaignRef, PreCampaignProps>(
  (
    {
      jobConfigId,
      fileData,
      onValuesChange,
      initialValues,
      setFileUploaded,
      setCampaignDate,
      mode,
      selectedMarketsIncludes,
      setSelectedMarketsIncludes,
      scmType,
      setScmType,
      isSales,
      setIsSales,
      viewMode = false,
    },
    ref
  ) => {
    const [searchParams] = useSearchParams();
    const { id: urlId } = useParams<{ id: string }>();
    const edit = searchParams.get("edit");
    const initialName = searchParams.get("name");
    const geoLiftJobConfigService = new GeoLiftJobConfigService();
    const initialValuesRef = useRef(initialValues);
    const isMounted = useRef(false);
    const [geoLiftStudyName, setTestName] = useState(initialName || "");
    const [buyType, setBuyType] = useState<"national" | "non-national">(() => {
      if (initialValues?.buyType) {
        return initialValues.buyType;
      }
      return "national";
    });
    const [loading, setLoading] = useState(false);
    const [csvData, setCsvData] = useState<any[]>([]);
    const [columns, setColumns] = useState<
      { title: string; dataIndex: string }[]
    >([]);
    const [fileList, setFileList] = useState<File[]>(
      initialValues?.fileList || []
    );
    const result = summarizeMarkets(csvData);
    const [validationResult, setValidationResult] =
      useState<ValidationResult | null>(null);
    const [showMappingModal, setShowMappingModal] = useState(false);
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [columnMappings, setColumnMappings] = useState<{
      [key: string]: string | null;
    }>({});
    const [campaignStartDate, setCampaignStartDate] = useState<Dayjs | null>(
      null
    );
    const [isAiQcEnabled, setIsAiQcEnabled] = useState(initialValues?.is_ai_qc_enabled || false);
    const getCurrectGeneratedJob = useSelector(
      (state: RootState) => state.geoLift.jobConfigList
    );
    useEffect(() => {
      // Populate data for both view and edit modes when jobConfigId exists
      if (jobConfigId && getCurrectGeneratedJob.length > 0) {
        const date: JobConfig = getCurrectGeneratedJob.find(
          (ele) => ele?.jobConfigId === jobConfigId
        );
        if (date) {
          const parsedCampaignDate = date.modelParameterConfig.parameters.campaignStartDate
            ? dayjs(date.modelParameterConfig.parameters.campaignStartDate)
            : null;
          setCampaignStartDate(parsedCampaignDate);
          setCampaignDate(parsedCampaignDate);
          setSelectedMarketsIncludes(
            date?.modelParameterConfig?.parameters?.includeMarket || []
          );
          setScmType(
            date?.modelParameterConfig?.parameters?.scmType || "single"
          );
          setIsSales(
            date?.modelParameterConfig?.parameters?.isSales ?? true
          )

          setTestName(date?.name || "");

          // Sync Redux value to local state
          setIsAiQcEnabled(!!date.is_ai_qc_enabled);
          setBauSpent(
            date.modelParameterConfig?.parameters?.bauSpent
              ? String(date.modelParameterConfig.parameters.bauSpent)
              : "0"
          );
          setStatSigThreshold(String(date.modelParameterConfig.parameters.statSigThreshold ?? DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD))
        }
      }
    }, [jobConfigId, getCurrectGeneratedJob]);
    const [bauSpent, setBauSpent] = useState<string>("");
    const [dataSource, setDataSource] = useState<"csv" | "database">("csv");
    const [loadingDatabase, setLoadingDatabase] = useState(false);
    const [showDatabaseModal, setShowDatabaseModal] = useState(false);
    const [statSigThreshold, setStatSigThreshold] = useState<string>(`${DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD}`);

    useEffect(() => {
      console.log("initialValues", initialValues)
      if (initialValuesRef.current !== initialValues) {
        // Only adopt the incoming name if it's non-empty. The jobConfigList
        // effect above is the authoritative source for edit/view mode and
        // always sets a real name; if we also overwrite from initialValues
        // whenever its (often-stale/empty) geoLiftStudyName changes, the two
        // effects fight and the Test Name input flickers between "" and the
        // real name.
        if (initialValues?.geoLiftStudyName) {
          setTestName(initialValues.geoLiftStudyName);
        }
        setBuyType(initialValues?.buyType || "non-national");
        setFileList(initialValues?.fileList || []);
        if (initialValues?.isSales !== undefined && initialValues?.isSales !== null) {
          setIsSales(initialValues.isSales);
        }
        initialValuesRef.current = initialValues;
      }
    }, [initialValues]);

    useEffect(() => {
      if (isMounted.current) {
        const modeBuyType = "national";
        setBuyType(modeBuyType);
        if (onValuesChange) {
          onValuesChange({
            geoLiftStudyName,
            buyType: modeBuyType,
            fileList,
            columnMappings,
            campaignStartDate: campaignStartDate?.format("YYYY-MM-DD") || undefined,
            is_ai_qc_enabled: isAiQcEnabled,
            bauSpent: Number(bauSpent),
            statSigThreshold: Number(statSigThreshold || DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD),
            isSales: isSales,
          });
        }
      } else {
        isMounted.current = true;
        if (onValuesChange) {
          onValuesChange({
            geoLiftStudyName,
            buyType,
            fileList,
            columnMappings,
            campaignStartDate:
              campaignStartDate?.format("YYYY-MM-DD") || undefined,
            is_ai_qc_enabled: isAiQcEnabled,
            bauSpent: Number(bauSpent),
            statSigThreshold: Number(statSigThreshold || DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD),
            isSales: isSales,
          });
        }
      }
    }, [mode, geoLiftStudyName, fileList, columnMappings, campaignStartDate, isAiQcEnabled, bauSpent, statSigThreshold]);

    useImperativeHandle(ref, () => ({
      getData: () => ({
        geoLiftStudyName,
        buyType,
        fileList,
        campaignStartDate: campaignStartDate?.format("YYYY-MM-DD"),
        bauSpent: Number(bauSpent),
        is_ai_qc_enabled: isAiQcEnabled,
        statSigThreshold: Number(statSigThreshold || DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD),
        isSales: isSales,
      }),
    }));

    const handleDateChange = (date: Dayjs | null) => {
      if (date) {
        const selectedDate = date.endOf("day");
        setCampaignStartDate(selectedDate);
        setCampaignDate(selectedDate);
      } else {
        setCampaignStartDate(null);
        setCampaignDate(null);
      }
    };
    const checkPostTestData = async () => {
      if (!jobConfigId) return;
      processLocationsAfterUpload();
      setLoading(true);

      try {
        // 1️⃣ Fetch CSV as Blob
        const fileResponse = await axiosInstance.get(
          `/api/s3/getFile?path=${jobConfigId}/input/geo_lift_data_full.csv`,
          {
            responseType: "blob",
          }
        );

        // 2️⃣ Create File from Blob (same as upload)
        const csvFile = new File(
          [fileResponse.data],
          "geo_lift_data_full.csv",
          {
            type: "text/csv",
          }
        );

        // 3️⃣ Parse CSV (inline, same logic as upload)
        Papa.parse<string[]>(csvFile, {
          skipEmptyLines: true,
          complete: (result) => {
            const data = result.data;

            if (!data || data.length === 0) {
              message.error("Empty CSV or invalid format.");
              setFileUploaded?.(false);
              return;
            }

            const headers = data[0];

            const generatedColumns = headers.map((header) => ({
              title: header,
              dataIndex: header.toLowerCase().replace(/\s+/g, "_"),
            }));

            const tableData = data.slice(1).map((row, index) => ({
              key: index,
              ...headers.reduce((acc, header, i) => {
                acc[header.toLowerCase().replace(/\s+/g, "_")] = row[i];
                return acc;
              }, {} as Record<string, any>),
            }));

            setColumns(generatedColumns);
            setCsvData(tableData);
            setFileList([csvFile]);
            setFileUploaded?.(true);
            if (edit !== "true") {
              message.success("Post-test CSV loaded successfully");
            }
          },
          error: (error) => {
            console.error("CSV parsing error:", error);
            setCsvData([]);
            setFileUploaded?.(false);
          },
        });
      } catch (error: any) {
        // Silently handle 404/500 errors for new jobs (file doesn't exist yet)
        // Only show error for actual failures in edit/view mode
        const status = error?.response?.status;
        if (status !== 404 && status !== 500) {
          console.error("Error checking post-test data:", error);
          message.error("Failed to load post-test CSV");
        }
        setFileUploaded?.(false);
      } finally {
        setLoading(false);
      }
    };

    const disabledDate = (current: Dayjs) => {
      return current && current > dayjs().endOf("day");
    };

    const handleBauSpentChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      const value = e.target.value;

      // Allow empty while editing
      if (value === "") {
        setBauSpent("");
        return;
      }

      // Allow numbers with up to 2 decimal places
      if (!/^\d*\.?\d{0,2}$/.test(value)) {
        return;
      }

      const numericValue = Number(value);

      // Prevent negative values
      if (numericValue < 0) {
        return;
      }

      setBauSpent(value);
    };

    const handleStatSignThresholdChange = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      const value = e.target.value;

      // Allow empty while editing
      if (value === "") {
        setStatSigThreshold("");
        return;
      }

      // Allow only numbers with max 2 decimal places
      if (!/^\d*\.?\d{0,2}$/.test(value)) {
        return;
      }

      const numericValue = Number(value);

      // Prevent values greater than 100 only
      if (numericValue > 100) {
        return;
      }

      setStatSigThreshold(value);
    };

    const handleLoadFromDatabase = async (params: {
      startDate: string;
      endDate: string;
      aggregationLevel: 'city' | 'state' | 'dma';
    }) => {
      if (!jobConfigId) {
        message.error("Job configuration is not initialized");
        return;
      }

      setLoadingDatabase(true);
      setLoading(true);
      try {
        const response = await axiosInstance.post(
          `/model/geo_lift/loadFromDatabaseAndProcess/${jobConfigId}`,
          null,
          {
            params: {
              start_date: params.startDate,
              end_date: params.endDate,
              aggregation_level: params.aggregationLevel
            }
          }
        );

        if (response.data?.success && response.data?.data) {
          // Validate data sufficiency
          const MIN_LOCATIONS = 5;
          const MIN_TIME_PERIODS = 40;
          const uniqueLocations = response.data.summary?.unique_locations || 0;
          const uniqueDates = response.data.summary?.unique_dates || 0;

          if (uniqueLocations < MIN_LOCATIONS || uniqueDates < MIN_TIME_PERIODS) {
            Modal.error({
              title: "Insufficient Data",
              content: (
                <div>
                  <p>Your data does not meet the minimum requirements for Geo-Lift analysis:</p>
                  <ul style={{ marginTop: 12, paddingLeft: 20 }}>
                    <li>
                      <strong>Locations:</strong> {uniqueLocations} found (minimum {MIN_LOCATIONS} required)
                      {uniqueLocations < MIN_LOCATIONS && (
                        <span style={{ color: "var(--error)" }}> - Insufficient</span>
                      )}
                    </li>
                    <li>
                      <strong>Time Periods:</strong> {uniqueDates} found (minimum {MIN_TIME_PERIODS} required)
                      {uniqueDates < MIN_TIME_PERIODS && (
                        <span style={{ color: "var(--error)" }}> - Insufficient</span>
                      )}
                    </li>
                  </ul>
                  <p style={{ marginTop: 12 }}>
                    Please select a wider date range or use a different aggregation level.
                  </p>
                </div>
              ),
              okText: "Got it",
            });
            setLoadingDatabase(false);
            setLoading(false);
            return;
          }

          setLocationData(response.data.data);

          // Convert location data to CSV format for display
          const csvRows = response.data.data.map((loc: any) => ({
            location: loc.locationOriginal,
            date: loc.date || 'N/A',
            Y: loc.Y || 0
          }));

          setCsvData(csvRows);
          setColumns([
            { title: 'Location', dataIndex: 'location' },
            { title: 'Date', dataIndex: 'date' },
            { title: 'Revenue (Y)', dataIndex: 'Y' }
          ]);

          // Mark as uploaded
          setFileUploaded?.(true);
          setShowDatabaseModal(false);

          message.success(
            `Loaded ${response.data.summary.total_records} records from database (${params.startDate} to ${params.endDate})`
          );
        } else {
          message.error("Failed to load data from database");
        }
      } catch (error: any) {
        console.error("Error loading from database:", error);
        message.error(
          error?.response?.data?.detail || "Failed to load data from database"
        );
        setFileUploaded?.(false);
      } finally {
        setLoadingDatabase(false);
        setLoading(false);
      }
    };

    useEffect(() => {
      if (initialValues?.fileList.length) {
        const file: any = initialValues.fileList[0];
        if (file) {
          Papa.parse(file, {
            complete: (result) => {
              if (result.data.length > 0) {
                const headers = result.data[0] as string[];
                const generatedColumns = headers.map((header) => ({
                  title: header,
                  dataIndex: header.toLowerCase().replace(/\s+/g, "_"),
                }));

                const tableData = result.data
                  .slice(1)
                  .map((row: any, index) => ({
                    key: index,
                    ...headers.reduce((acc, header, i) => {
                      acc[header.toLowerCase().replace(/\s+/g, "_")] = row[i];
                      return acc;
                    }, {} as Record<string, any>),
                  }));

                setColumns(generatedColumns);
                setCsvData(tableData);
              }
            },
            error: (error) => {
              console.error("CSV parsing error:", error);
              setCsvData([]);
            },
            skipEmptyLines: true,
          });
        }
      }
    }, [initialValues?.fileList]);

    useEffect(() => {
      if (fileList.length > 0 && csvData.length === 0) {
        const file = fileList[0];
        if (file) {
          Papa.parse(file, {
            complete: (result) => {
              if (result.data.length > 0) {
                const headers = result.data[0] as string[];
                const generatedColumns = headers.map((header) => ({
                  title: header,
                  dataIndex: header.toLowerCase().replace(/\s+/g, "_"),
                }));

                const tableData = result.data
                  .slice(1)
                  .map((row: any, index) => ({
                    key: index,
                    ...headers.reduce((acc, header, i) => {
                      acc[header.toLowerCase().replace(/\s+/g, "_")] = row[i];
                      return acc;
                    }, {} as Record<string, any>),
                  }));

                setColumns(generatedColumns);
                setCsvData(tableData);
              }
            },
            error: (error) => {
              console.error("CSV parsing error:", error);
              setCsvData([]);
            },
            skipEmptyLines: true,
          });
        }
      }
    }, [fileList]);

    const handleTestNameChange = (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setTestName(newValue);
    };

    const handleFileUpload = async (
      file: File,
      mappings: { [key: string]: string | null }
    ) => {
      setLoading(true);
      try {
        await geoLiftJobConfigService.uploadFile(
          file,
          `${jobConfigId}/input/geo_lift_data.csv`
        );
        await new Promise((resolve, reject) => {
          Papa.parse(file, {
            complete: (result) => {
              if (result.data.length > 0) {
                const headers = result.data[0] as string[];
                const generatedColumns = headers.map((header) => ({
                  title: header,
                  dataIndex: header.toLowerCase().replace(/\s+/g, "_"),
                }));

                const tableData = result.data
                  .slice(1)
                  .map((row: any, index) => ({
                    key: index,
                    ...headers.reduce((acc, header, i) => {
                      acc[header.toLowerCase().replace(/\s+/g, "_")] = row[i];
                      return acc;
                    }, {} as Record<string, any>),
                  }));

                setColumns(generatedColumns);
                setCsvData(tableData);
                setFileList([file]);
                setFileUploaded?.(true);
                setColumnMappings(mappings);

                message.success(
                  `${file.name} uploaded and mapped successfully`
                );

                resolve(true);
              } else {
                message.error("Empty CSV or invalid format.");
                setFileUploaded?.(false);
                reject(new Error("Empty CSV"));
              }
            },
            error: (error) => {
              console.error("CSV parsing error:", error);
              setCsvData([]);
              setFileUploaded?.(false);
              reject(error);
            },
            skipEmptyLines: true,
          });
        });
      } catch (error) {
        console.error("Upload failed:", error);
        message.error("File upload failed");
        setFileUploaded?.(false);
        throw error;
      } finally {
        setLoading(false);
      }
    };

    const handleMappingConfirm = async (mappings: ColumnMapping[]) => {
      if (!pendingFile) {
        message.error("No file selected");
        return;
      }

      const backendMappings = convertMappingsToBackendFormat(mappings);
      setShowMappingModal(false);

      try {
        await handleFileUpload(pendingFile, backendMappings);
        setTimeout(async () => {
          await processLocationsAfterUpload();
        }, 2000);
      } catch (error) {
        console.error("Upload failed after mapping:", error);
      }
    };

    const handleMappingCancel = () => {
      setShowMappingModal(false);
      setPendingFile(null);
      setValidationResult(null);
      message.info("File upload cancelled");
    };

    const uploadProps: UploadProps = {
      name: "file",
      accept: ".csv",
      showUploadList: false, // Add this line
      beforeUpload: async (file) => {
        setLoading(true);
        try {
          await new Promise((resolve, reject) => {
            Papa.parse(file, {
              preview: 1,
              header: true,
              complete: (result) => {
                if (result.meta.fields && result.meta.fields.length > 0) {
                  const headers = result.meta.fields;
                  const validation = validateGeoLiftData(headers);
                  setValidationResult(validation);
                  setPendingFile(file);
                  setShowMappingModal(true);
                  setLoading(false);
                  resolve(true);
                } else {
                  message.error("Empty CSV or invalid format.");
                  setFileUploaded?.(false);
                  setLoading(false);
                  reject(new Error("Empty CSV"));
                }
              },
              error: (error) => {
                console.error("CSV parsing error:", error);
                setFileUploaded?.(false);
                setLoading(false);
                reject(error);
              },
              skipEmptyLines: true,
            });
          });
        } catch (error) {
          console.error("Upload failed:", error);
          message.error("File upload failed");
          setFileUploaded?.(false);
          setLoading(false);
        }

        return false;
      },
    };

    type RawItem = { location: string; totalSales: number; count: number };
    type StateDataItem = { state: string; value: number };
    type CityDataItem = {
      name: string;
      coordinates: [number, number];
      value: number;
    };

    const US_STATE_ABBR = new Set([
      "AL",
      "AK",
      "AZ",
      "AR",
      "CA",
      "CO",
      "CT",
      "DC",
      "DE",
      "FL",
      "GA",
      "HI",
      "IA",
      "ID",
      "IL",
      "IN",
      "KS",
      "KY",
      "LA",
      "MA",
      "MD",
      "ME",
      "MI",
      "MN",
      "MO",
      "MS",
      "MT",
      "NC",
      "ND",
      "NE",
      "NH",
      "NJ",
      "NM",
      "NV",
      "NY",
      "OH",
      "OK",
      "OR",
      "PA",
      "RI",
      "SC",
      "SD",
      "TN",
      "TX",
      "UT",
      "VA",
      "VT",
      "WA",
      "WI",
      "WV",
      "WY",
    ]);

    const REGION_TO_STATES: Record<string, string[]> = {
      "New England": ["MA", "CT", "RI", "NH", "VT", "ME"],
      "West Texas/New Mexico": ["TX", "NM"],
    };

    const LOCATION_OVERRIDES: Record<
      string,
      { cities?: Array<{ city: string; state: string }>; states?: string[] }
    > = {
      "Baltimore/Washington": {
        cities: [
          { city: "Baltimore", state: "MD" },
          { city: "Washington", state: "DC" },
        ],
      },
      "New Orleans/Mobile": {
        cities: [
          { city: "New Orleans", state: "LA" },
          { city: "Mobile", state: "AL" },
        ],
      },
      "West Texas/New Mexico": { states: ["TX", "NM"] },
      "New England": { states: ["MA", "CT", "RI", "NH", "VT", "ME"] },
    };

    const cleanTail = (s: string) =>
      s.replace(/\s*-\s*MultiOutletC\s*$/i, "").trim();

    const hash32 = (s: string) => {
      let h = 2166136261 >>> 0;
      for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      return h >>> 0;
    };

    const seeded01 = (seedStr: string) => {
      let h = hash32(seedStr);
      h ^= h << 13;
      h ^= h >>> 17;
      h ^= h << 5;
      return ((h >>> 0) % 1_000_000) / 1_000_000;
    };

    const randomUSCoord = (key: string): [number, number] => {
      const r1 = seeded01(key + "::lon");
      const r2 = seeded01(key + "::lat");
      const lon = -124.7 + r1 * 57.8;
      const lat = 24.5 + r2 * 25.0;
      return [parseFloat(lon.toFixed(4)), parseFloat(lat.toFixed(4))];
    };

    const expandMultiCitySameState = (cityPart: string, state: string) =>
      cityPart
        .split("/")
        .map((c) => ({ city: c.trim().replace(/\s+/g, " "), state }));

    const tryParseCityState = (
      text: string
    ): Array<{ city: string; state: string }> | null => {
      const m = text.match(/^(.*?),\s*([A-Z]{2})$/i);
      if (!m) return null;
      const rawCity = m[1].trim();
      const state = m[2].toUpperCase();
      if (!US_STATE_ABBR.has(state)) return null;
      return expandMultiCitySameState(rawCity, state);
    };

    const tryParseStateOnly = (text: string): string | null => {
      const m = text.match(/^[A-Za-z.\s]+,\s*([A-Z]{2})$/);
      if (!m) return null;
      const st = m[1].toUpperCase();
      return US_STATE_ABBR.has(st) ? st : null;
    };

    const normalizeLocation = (
      raw: string
    ): {
      cities?: Array<{ city: string; state: string }>;
      states?: string[];
    } => {
      const cleaned = cleanTail(raw);

      if (LOCATION_OVERRIDES[cleaned]) return LOCATION_OVERRIDES[cleaned];

      if (REGION_TO_STATES[cleaned])
        return { states: REGION_TO_STATES[cleaned] };

      const cities = tryParseCityState(cleaned);
      if (cities?.length) return { cities };

      const st = tryParseStateOnly(cleaned);
      if (st) return { states: [st] };

      // Check if it's a full state name (e.g., "California", "New York")
      const stateCode = STATE_NAME_TO_POSTAL[cleaned];
      if (stateCode) return { states: [stateCode] };

      return {};
    };

    function transformSalesToStateAndCity(rows: RawItem[]): {
      US_STATE_DATA: StateDataItem[];
      US_CITY_DATA: CityDataItem[];
    } {
      const stateTotals = new Map<string, number>();
      const cityPoints: CityDataItem[] = [];

      for (const row of rows) {
        const parsed = normalizeLocation(row.location);
        const total = row.totalSales ?? 0;
        if (parsed.cities?.length) {
          const cityShare = total / parsed.cities.length;

          const statesInRow = Array.from(
            new Set(parsed.cities.map((c) => c.state))
          );
          const stateShare = total / statesInRow.length;

          for (const st of statesInRow) {
            stateTotals.set(st, (stateTotals.get(st) ?? 0) + stateShare);
          }

          for (const { city, state } of parsed.cities) {
            const name = `${city}, ${state}`;
            const coordinates = randomUSCoord(name);
            cityPoints.push({ name, coordinates, value: cityShare });
          }
        } else if (parsed.states?.length) {
          const perState = total / parsed.states.length;
          for (const st of parsed.states) {
            stateTotals.set(st, (stateTotals.get(st) ?? 0) + perState);
          }
        }
      }

      const US_STATE_DATA: StateDataItem[] = Array.from(stateTotals.entries())
        .map(([state, value]) => ({ state, value }))
        .sort((a, b) => b.value - a.value);

      const US_CITY_DATA: CityDataItem[] = cityPoints.sort(
        (a, b) => b.value - a.value
      );

      return { US_STATE_DATA, US_CITY_DATA };
    }

    const { US_STATE_DATA } = transformSalesToStateAndCity(result);
    const [locationData, setLocationData] = useState<any[]>([]);
    const [loadingLocations, setLoadingLocations] = useState(false);

    const processLocationsAfterUpload = async () => {
      if (!jobConfigId) {
        return;
      }

      setLoadingLocations(true);
      try {
        const response = await axiosInstance.post(
          `/model/geo_lift/processLocations/${jobConfigId}`
        );

        if (response.data?.success && response.data?.data) {
          setLocationData(response.data.data);
          getPreselectedTestMarkets();
          if (response.data.warning) {
            message.warning(response.data.warning);
          }
        }
      } catch (err: any) {
        // Silently handle 404/500 errors for new jobs (file doesn't exist yet)
        const status = err?.response?.status;
        if (status !== 404 && status !== 500) {
          console.error("Error processing location data:", err);
          message.error("Failed to process location data");
        }
      } finally {
        setLoadingLocations(false);
      }
    };

    const cityDataForMap = useMemo(() => {
      if (!locationData || locationData.length === 0) {
        console.log('[cityDataForMap] No locationData available');
        return [];
      }

      console.log('[cityDataForMap] Processing', locationData.length, 'locations');
      console.log('[cityDataForMap] Sample location:', locationData[0]);

      // process_locations returns `lat`/`long` for the state fast-path and
      // `latitude`/`longitude` for the general geocoding path — normalize both.
      const mappedData = locationData
        .map((loc) => ({
          ...loc,
          lat: loc.lat ?? loc.latitude,
          long: loc.long ?? loc.longitude,
        }))
        .filter((loc) => loc.lat && loc.long)
        .map((loc, index) => {
          let salesValue = 0;

          // If location data already has Y value (from database load or CSV upload), use it
          if (loc.Y !== undefined && loc.Y !== null) {
            salesValue = loc.Y;
            if (index < 3) {
              console.log(`[cityDataForMap] Location ${index}: Using Y value:`, salesValue, 'for', loc.locationOriginal);
            }
          } else {
            // Otherwise, try to match with CSV data (for file uploads)
            const marketSales = result || [];
            for (const market of marketSales) {
              if (market.location && loc.locationOriginal) {
                if (fuzzyMatchLocation(market.location, loc.locationOriginal)) {
                  salesValue = market.totalSales || 0;
                  if (index < 3) {
                    console.log(`[cityDataForMap] Location ${index}: Matched with CSV:`, salesValue, 'for', loc.locationOriginal);
                  }
                  break;
                }
              }
            }
            if (salesValue === 0 && index < 3) {
              console.log(`[cityDataForMap] Location ${index}: No match found for`, loc.locationOriginal);
            }
          }

          // Extract state name from locationAddress for map matching
          // locationAddress format: "City, State Abbr, Country" or full address with state
          let stateName = "Unknown";

          // Create reverse mapping: abbreviation -> full name
          const POSTAL_TO_STATE_NAME: Record<string, string> = {};
          for (const [fullName, abbr] of Object.entries(STATE_NAME_TO_POSTAL)) {
            POSTAL_TO_STATE_NAME[abbr] = fullName;
          }

          if (loc.locationAddress) {
            // Try to extract state from address (e.g., "Atlanta, GA, United States" -> "Georgia")
            const addressParts = loc.locationAddress.split(',').map((p: string) => p.trim());

            // Look for state abbreviation or full state name in address parts
            for (const part of addressParts) {
              // Check if it's a 2-letter state code (e.g., "GA", "NC")
              if (part.length === 2 && part === part.toUpperCase()) {
                const fullStateName = POSTAL_TO_STATE_NAME[part];
                if (fullStateName) {
                  stateName = fullStateName;
                  break;
                }
              }
              const stateNamesLowercase = Object.keys(STATE_NAME_TO_POSTAL).map(
                (state) => state.toLowerCase()
              );
              // Check if it's already a full state name
              if (stateNamesLowercase.includes(part)) {
                stateName = part;
                break;
              }
            }
          }

          if (index < 3) {
            console.log(`[cityDataForMap] Location ${index}: Extracted state name "${stateName}" from "${loc.locationAddress}"`);
          }

          return {
            name: stateName,
            coordinates: [loc.long, loc.lat] as [number, number],
            value: salesValue > 0 ? salesValue : 100,
            isState: true,
          };
        });

      console.log('[cityDataForMap] Total mapped locations:', mappedData.length);
      console.log('[cityDataForMap] Sample mapped data:', mappedData.slice(0, 3));

      return mappedData;
    }, [locationData, result]);
    const getPreselectedTestMarkets = async (forceRefresh = false) => {
      try {
        const payload: Record<string, any> = { job_config_id: jobConfigId };
        if (campaignStartDate) {
          payload.start_campaign_date = new Date(campaignStartDate as any)
            .toISOString()
            .split("T")[0];
        }
        if (forceRefresh) {
          payload.force_refresh = true;
        }
        const response = await axiosInstance.post(GEO_LIFT_GET_TEST_MARKET, payload);
        const arrayOfStrings: string[] = Object.values(
          response.data.market_names
        );
        setSelectedMarketsIncludes(
          arrayOfStrings?.map((ele) => ele?.toLocaleLowerCase())
        );
      } catch (err: any) {
        console.log(err);
        if (err?.response?.status === 404) {
          setSelectedMarketsIncludes([]);
          message.error(
            err?.response?.data?.error ||
              "Campaign start date was not found in your uploaded data. Please double-check the date and try again.",
            6
          );
        }
      }
    };

    // Re-fetch test markets with the correct pre-campaign window whenever the
    // campaign start date changes after a file has already been uploaded.
    const isFileUploaded = fileList.length > 0;
    useEffect(() => {
      if (!isFileUploaded || !campaignStartDate || !jobConfigId) return;
      getPreselectedTestMarkets(true);
    }, [campaignStartDate]);

    useEffect(() => {
      // Load CSV data for edit mode AND view mode (existing jobs)
      // For new jobs: mode=evaluate or mode=plan is in URL (and edit is NOT true)
      // For edit mode: edit=true is in URL
      // For view mode: no mode parameter and no edit parameter
      const modeParam = searchParams.get("mode");
      const isEditMode = edit === "true";
      const isNewJob = !!modeParam && !isEditMode; // New job has mode but NOT edit=true
      const isViewMode = !modeParam && !isEditMode; // View mode has neither mode nor edit

      if (jobConfigId && (isEditMode || isViewMode)) {
        checkPostTestData();
      }
    }, [jobConfigId, edit, searchParams]);
    return (
      <div
        className=" mx-auto px-4 sm:px-2 lg:px-3 py-2 space-y-4"
        style={viewMode ? { pointerEvents: "none" } : undefined}
      >
        {/* Header Info Banner */}
        <div className="bg-[var(--blue-subtle)] rounded-xl p-6 shadow-sm border border-[var(--border-subtle)]">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 mt-1">
              <div className="w-12 h-12 bg-[var(--blue)] rounded-lg flex items-center justify-center shadow-md">
                <CustomExcelIcon className="text-white text-2xl" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
                Upload Campaign Data
              </h3>
              <div className="space-y-1 text-sm text-[var(--text-secondary)]">
                <p className="flex items-center gap-2">
                  <CheckCircleOutlined className="text-[var(--blue)]" />
                  Upload a CSV file containing conversion data before the
                  campaign started
                </p>
                <p className="flex items-center gap-2">
                  <CheckCircleOutlined className="text-[var(--blue)]" />
                  Format: <span className="font-semibold">CSV only</span>, with
                  column headers in the first row
                </p>
              </div>
            </div>
          </div>
        </div>

        {mode === "evaluate" || edit === "true" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Test Name Input */}
            <div className="bg-[var(--bg-elevated)] rounded-xl p-6 shadow-sm border border-[var(--border-default)] hover:shadow-md transition-shadow">
              <label className="flex items-center gap-2 text-[1rem] font-semibold text-[var(--text-primary)] mb-3">
                <span className="w-8 h-8 bg-[var(--blue-subtle)] rounded-lg flex items-center justify-center text-[var(--blue)] font-bold text-sm">
                  1
                </span>
                Test Name
                <span className="text-[var(--error)]">*</span>
              </label>
              <Input
                placeholder="e.g., Synthetic Control Test Q4 2024"
                value={geoLiftStudyName}
                onChange={handleTestNameChange}
                size="large"
                className="rounded-lg"
                autoComplete="off"
              />
            </div>

            {/* Campaign Start Date */}
            <div className="bg-[var(--bg-elevated)] rounded-xl p-6 shadow-sm border border-[var(--border-default)] hover:shadow-md transition-shadow">
              <label className="flex items-center gap-2 text-[1rem] font-semibold text-[var(--text-primary)] mb-3">
                <span className="w-8 h-8 bg-[var(--blue-subtle)] rounded-lg flex items-center justify-center text-[var(--blue)] font-bold text-sm">
                  2
                </span>
                <CalendarOutlined className="text-[var(--blue)]" />
                Campaign Start Date
                <span className="text-[var(--error)]">*</span>
              </label>
              <DatePicker
                placeholder="Select when your campaign started"
                value={campaignStartDate}
                onChange={handleDateChange}
                disabledDate={disabledDate}
                size="large"
                className="w-full rounded-lg"
                format="YYYY-MM-DD"
                showToday={false}
              />
              <Text className="text-[var(--text-secondary)] text-sm mt-2 block">
                Select the date when your campaign started (past dates only)
              </Text>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Test Name */}
            <div className="bg-[var(--bg-elevated)] rounded-xl p-6 shadow-sm border border-[var(--border-default)] hover:shadow-md transition-shadow">
              <label className="flex items-center gap-2 text-[1rem] font-semibold text-[var(--text-primary)] mb-3">
                <span className="w-8 h-8 bg-[var(--blue-subtle)] rounded-lg flex items-center justify-center text-[var(--blue)] font-bold text-sm">
                  1
                </span>
                Test Name
                <span className="text-[var(--error)]">*</span>
              </label>
              <Input
                placeholder="e.g., Synthetic Control Test Q4 2024"
                value={geoLiftStudyName}
                onChange={handleTestNameChange}
                size="large"
                className="rounded-lg"
                autoComplete="off"
              />
            </div>

            {/* Planned Campaign Start Date */}
            <div className="bg-[var(--bg-elevated)] rounded-xl p-6 shadow-sm border border-[var(--border-default)] hover:shadow-md transition-shadow">
              <label className="flex items-center gap-2 text-[1rem] font-semibold text-[var(--text-primary)] mb-3">
                <span className="w-8 h-8 bg-[var(--blue-subtle)] rounded-lg flex items-center justify-center text-[var(--blue)] font-bold text-sm">
                  2
                </span>
                <CalendarOutlined className="text-[var(--blue)]" />
                Planned Campaign Start Date
                <span className="text-[var(--error)]">*</span>
              </label>
              <DatePicker
                placeholder="Select your planned campaign start date"
                value={campaignStartDate}
                onChange={handleDateChange}
                size="large"
                className="w-full rounded-lg"
                format="YYYY-MM-DD"
                showToday={false}
              />
              <Text className="text-[var(--text-secondary)] text-sm mt-2 block">
                Select when your planned campaign will start
              </Text>
            </div>
          </div>
        )}

        {/* BAU Spent Input */}
        <div className="bg-[var(--bg-elevated)] rounded-xl p-6 shadow-sm border border-[var(--border-default)] hover:shadow-md transition-shadow">
          <label className="flex items-center gap-2 text-[1rem] font-semibold text-[var(--text-primary)] mb-3">
            <span className="w-8 h-8 bg-[var(--blue-subtle)] rounded-lg flex items-center justify-center text-[var(--blue)] font-bold text-sm">
              3
            </span>
              <DollarOutlined className="text-[var(--success)]" />
                Average Monthly BAU Media Spent
              <span className="text-[var(--error)]">*</span>
          </label>
          <Input
            placeholder="Enter Average Monthly BAU Media Spent (e.g., 2000.50)"
            value={bauSpent}
            onChange={handleBauSpentChange}
            prefix={<span className="text-[var(--text-secondary)] font-medium">$</span>}
            size="large"
            type="text"
            className="rounded-lg"
          />
        </div>

        {/* Statistical Significance Threshold Input */}
        <div className="bg-[var(--bg-elevated)] rounded-xl p-6 shadow-sm border border-[var(--border-default)] hover:shadow-md transition-shadow">
          <label className="flex items-center gap-2 text-[1rem] font-semibold text-[var(--text-primary)] mb-3">
            <span className="w-8 h-8 bg-[var(--blue-subtle)] rounded-lg flex items-center justify-center text-[var(--blue)] font-bold text-sm">
              {mode === "evaluate" ? "4" : "3"}
            </span>
              Statistical Significance Threshold %
          </label>
          <Input
            placeholder="Enter Statistical Significance Threshold % (50 to 100 inclusive)"
            value={statSigThreshold}
            onChange={handleStatSignThresholdChange}
            onBlur={() => {
              if (statSigThreshold === "") {
                setStatSigThreshold(
                  `${DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD}`
                );
                return;
              }

              const numericValue = Number(statSigThreshold);

              if (numericValue < 50) {
                setStatSigThreshold("50");
              }
            }}
            size="large"
            type="text"
            className="rounded-lg"
          />
        </div>

        {/* Data Source Selection */}
        <div className="bg-[var(--bg-elevated)] rounded-xl p-6 shadow-sm border border-[var(--border-default)] hover:shadow-md transition-shadow">
          <label className="flex items-center gap-2 text-[1rem] font-semibold text-[var(--text-primary)] mb-4">
            <span className="w-8 h-8 bg-[var(--blue-subtle)] rounded-lg flex items-center justify-center text-[var(--blue)] font-bold text-sm">
              {mode === "evaluate" ? "5" : "4"}
            </span>
            Data Source
            <span className="text-[var(--error)]">*</span>
          </label>

          <Radio.Group
            onChange={(e) => setDataSource(e.target.value)}
            value={dataSource}
            className="w-full"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div
                className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
                  dataSource === "csv"
                    ? "border-[var(--blue)] bg-[var(--blue-subtle)] shadow-md"
                    : "border-[var(--border-default)] bg-[var(--bg-elevated)] hover:border-[var(--blue-hover)] hover:bg-[var(--bg-hover)]"
                }`}
              >
                <Radio value="csv" className="w-full">
                  <div className="ml-2">
                    <p className="font-semibold text-[var(--text-primary)] flex items-center gap-2">
                      <CustomExcelIcon className="text-[var(--success)]" />
                      Upload CSV File
                    </p>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">
                      Upload your own CSV with historical data
                    </p>
                  </div>
                </Radio>
              </div>

              <div
                className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
                  dataSource === "database"
                    ? "border-[var(--blue)] bg-[var(--blue-subtle)] shadow-md"
                    : "border-[var(--border-default)] bg-[var(--bg-elevated)] hover:border-[var(--blue-hover)] hover:bg-[var(--bg-hover)]"
                }`}
              >
                <Radio value="database" className="w-full">
                  <div className="ml-2">
                    <p className="font-semibold text-[var(--text-primary)] flex items-center gap-2">
                      <CalendarOutlined className="text-[var(--blue)]" />
                      Use Database Data
                    </p>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">
                      Load up to last 52 weeks of transaction data
                    </p>
                  </div>
                </Radio>
              </div>
            </div>
          </Radio.Group>
        </div>

        {/* Load from Database Button */}
        {dataSource === "database" && (
          <div className="bg-[var(--blue-subtle)] rounded-xl p-6 shadow-sm border border-[var(--border-default)]">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h4 className="font-semibold text-[var(--text-primary)] mb-2">
                  Database Data Source
                </h4>
                <p className="text-sm text-[var(--text-secondary)]">
                  Load transaction data from your database, automatically aggregated by location and date for Geo-Lift analysis. Default date range is 52 weeks (recommended).
                </p>
              </div>
              <Button
                type="primary"
                size="large"
                onClick={() => setShowDatabaseModal(true)}
                disabled={mode === "evaluate" && !campaignStartDate}
                className="ml-4"
              >
                Configure & Load
              </Button>
            </div>
            {mode === "evaluate" && !campaignStartDate && (
              <p className="text-sm text-[var(--warning)] mt-3 flex items-center gap-2 bg-[var(--warning-subtle)] p-3 rounded-lg">
                <span className="text-lg">⚠️</span>
                Please select campaign start date first
              </p>
            )}
          </div>
        )}

        {/* File Upload Section */}
        {dataSource === "csv" && (
          <div className="bg-[var(--bg-elevated)] rounded-xl p-6 shadow-sm border border-[var(--border-default)] hover:shadow-md transition-shadow">
            <label className="flex items-center gap-2 text-[1rem] font-semibold text-[var(--text-primary)] mb-4">
              <span className="w-8 h-8 bg-[var(--blue-subtle)] rounded-lg flex items-center justify-center text-[var(--blue)] font-bold text-sm">
                {mode === "evaluate" ? "6" : "5"}
              </span>
              Upload Data File
              <span className="text-[var(--error)]">*</span>
            </label>

          {/* Existing File Alert */}
          {fileData && (
            <div className="mb-4 p-4 bg-[var(--success-subtle)] rounded-lg border border-[var(--success)] flex items-start gap-3">
              <CheckCircleOutlined className="text-[var(--success)] text-xl mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-[var(--success)]">
                  Existing campaign data is loaded
                </p>
                <p className="text-xs text-[var(--success)] mt-1">
                  You can upload a new file to replace it
                </p>
              </div>
            </div>
          )}
          {edit !== "true" && (
            <div
              className={`border-2 border-dashed rounded-lg transition-all ${
                mode === "evaluate" && !campaignStartDate
                  ? "border-[var(--border-default)] bg-[var(--bg-surface)] cursor-not-allowed opacity-60"
                  : "border-[var(--border-default)] hover:border-[var(--blue)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)] cursor-pointer"
              }`}
            >
              <Upload
                {...uploadProps}
                disabled={mode === "evaluate" && !campaignStartDate}
              >
                <div className="flex items-center gap-4 py-6 px-6">
                  <div
                    className={`flex items-center justify-center w-12 h-12 rounded-lg flex-shrink-0 ${
                      mode === "evaluate" && !campaignStartDate
                        ? "bg-[var(--bg-hover)]"
                        : "bg-[var(--blue-subtle)]"
                    }`}
                  >
                    <UploadOutlined
                      className={`text-2xl ${
                        mode === "evaluate" && !campaignStartDate
                          ? "text-[var(--text-tertiary)]"
                          : "text-[var(--blue)]"
                      }`}
                    />
                  </div>
                  <div className="flex-1 text-left">
                    <div
                      className={`font-semibold text-[1rem] mb-1 ${
                        mode === "evaluate" && !campaignStartDate
                          ? "text-[var(--text-tertiary)]"
                          : "text-[var(--text-primary)]"
                      }`}
                    >
                      Click or drag file to upload
                    </div>
                    <p
                      className={`text-xs ${
                        mode === "evaluate" && !campaignStartDate
                          ? "text-[var(--text-tertiary)]"
                          : "text-[var(--text-secondary)]"
                      }`}
                    >
                      {mode === "evaluate" && !campaignStartDate
                        ? "Please select campaign start date first"
                        : "Support for CSV file only"}
                    </p>
                  </div>
                </div>
              </Upload>
            </div>
          )}

          {/* Uploaded File Display */}
          {fileList.length > 0 && (
            <div className="mt-4 p-4 bg-[var(--blue-subtle)] rounded-lg border border-[var(--border-default)] flex items-center justify-between hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[var(--blue)] rounded-lg flex items-center justify-center">
                  <CustomExcelIcon className="text-white text-xl" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[var(--text-primary)]">
                    {fileList[0].name}
                  </p>
                  <p className="text-xs text-[var(--text-secondary)]">
                    CSV File Uploaded Successfully
                  </p>
                </div>
              </div>
              <Button
                disabled={edit === "true"}
                type="text"
                danger
                onClick={() => {
                  setFileList([]);
                  setCsvData([]);
                  setLocationData([]);
                  setFileUploaded?.(false);
                }}
                icon={<CloseOutlined />}
                className="hover:bg-[var(--error-subtle)]"
              >
                Remove
              </Button>
            </div>
          )}

          {/* Data Preview Table */}
          {csvData.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[var(--text-primary)] flex items-center gap-2">
                  <span className="w-8 h-8 bg-[var(--violet-subtle)] rounded-lg flex items-center justify-center">
                    <CustomExcelIcon className="text-[var(--violet)]" />
                  </span>
                  Data Preview
                </h3>
                <span className="text-sm text-[var(--text-secondary)] bg-[var(--bg-hover)] px-3 py-1 rounded-full">
                  Showing first 5 rows
                </span>
              </div>
              <div className="overflow-x-auto rounded-lg border border-[var(--border-default)] shadow-sm">
                <Table
                  columns={columns}
                  loading={loading}
                  dataSource={csvData.slice(0, 5)}
                  pagination={false}
                  scroll={{ x: true }}
                  bordered
                  size="small"
                  className="w-full"
                />
              </div>
            </div>
          )}
          </div>
        )}

        {/* SCM Type Selection */}
        <div className="bg-[var(--bg-elevated)] rounded-xl p-6 shadow-sm border border-[var(--border-default)] hover:shadow-md transition-shadow">
          <label className="flex items-center gap-2 text-[1rem] font-semibold text-[var(--text-primary)] mb-4">
            <span className="w-8 h-8 bg-[var(--blue-subtle)] rounded-lg flex items-center justify-center text-[var(--blue)] font-bold text-sm">
              {mode === "evaluate" ? "7" : "6"}
            </span>
            SCM Type
            <span className="text-[var(--error)]">*</span>
          </label>

          <Radio.Group
            disabled={!locationData.length}
            onChange={(e) => {
              setScmType(e.target.value);
              setSelectedMarketsIncludes([]);
            }}
            value={scmType}
            className="w-full"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div
                className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
                  scmType === "pooled"
                    ? "border-[var(--blue)] bg-[var(--blue-subtle)] shadow-md"
                    : "border-[var(--border-default)] bg-[var(--bg-elevated)] hover:border-[var(--blue-hover)] hover:bg-[var(--bg-hover)]"
                } ${
                  !locationData.length ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <Radio value="pooled" className="w-full">
                  <div className="ml-2">
                    <p className="font-semibold text-[var(--text-primary)]">Pooled SCM</p>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">
                      Combine multiple markets for analysis
                    </p>
                  </div>
                </Radio>
              </div>

              <div
                className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
                  scmType === "single"
                    ? "border-[var(--blue)] bg-[var(--blue-subtle)] shadow-md"
                    : "border-[var(--border-default)] bg-[var(--bg-elevated)] hover:border-[var(--blue-hover)] hover:bg-[var(--bg-hover)]"
                } ${
                  !locationData.length ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <Radio value="single" className="w-full">
                  <div className="ml-2">
                    <p className="font-semibold text-[var(--text-primary)]">Single SCM</p>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">
                      Analyze individual markets separately
                    </p>
                  </div>
                </Radio>
              </div>
            </div>
          </Radio.Group>

          {!locationData.length && (
            <p className="text-sm text-[var(--warning)] mt-3 flex items-center gap-2 bg-[var(--warning-subtle)] p-3 rounded-lg">
              <span className="text-lg">⚠️</span>
              Please upload a file first to enable SCM type selection
            </p>
          )}
        </div>

        {/* Study Type Selection */}
        <div className="bg-[var(--bg-elevated)] rounded-xl p-6 shadow-sm border border-[var(--border-default)] hover:shadow-md transition-shadow">
          {/* Label */}
          <label className="flex items-center gap-2 text-[1rem] font-semibold text-[var(--text-primary)] mb-4">
            <span className="w-8 h-8 bg-[var(--blue-subtle)] rounded-lg flex items-center justify-center text-[var(--blue)] font-bold text-sm">
              {mode === "evaluate" ? "8" : "7"}
            </span>
            Sales
          </label>

          {/* Card */}
          <div
            className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all ${
              isSales
                ? "border-[var(--blue)] bg-[var(--blue-subtle)] shadow-md"
                : "border-[var(--border-default)] bg-[var(--bg-elevated)] hover:border-[var(--blue-hover)] hover:bg-[var(--bg-hover)]"
            } ${!locationData.length ? "opacity-50 cursor-not-allowed" : ""}`}
            onClick={() => {
              if (!locationData.length) return;
              setIsSales(!isSales);
            }}
          >
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={isSales}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  setIsSales(e.target.checked);
                }}
                className="w-5 h-5 accent-[var(--blue)] cursor-pointer"
                disabled={!locationData.length}
              />

              <div>
                <p className="font-semibold text-[var(--text-primary)]">Enable Sales</p>
                <p className="text-xs text-[var(--text-secondary)] mt-1">
                  {isSales
                    ? "Sales data will be included in upload"
                    : "Sales data will not be included in upload"}
                </p>
              </div>
            </div>
          </div>        
          {!locationData.length && (
            <p className="text-sm text-[var(--warning)] mt-3 flex items-center gap-2 bg-[var(--warning-subtle)] p-3 rounded-lg">
              <span className="text-lg">⚠️</span>
              Please upload a file first to enable Sales check
            </p>
          )}
        </div>

        {/* Market Selection - Only for Single SCM */}
        {scmType === "single" && (
          <div className="bg-[var(--bg-elevated)] rounded-xl p-6 shadow-sm border border-[var(--border-default)] hover:shadow-md transition-shadow animate-fadeIn">
            <label className="flex items-center gap-2 text-[1rem] font-semibold text-[var(--text-primary)] mb-4">
              <span className="w-8 h-8 bg-[var(--blue-subtle)] rounded-lg flex items-center justify-center text-[var(--blue)] font-bold text-sm">
                {mode === "evaluate" ? "9" : "8"}
              </span>
              Select Markets
              <span className="text-[var(--error)]">*</span>
            </label>
            <Select
              mode="multiple"
              placeholder="Choose one or more markets"
              value={selectedMarketsIncludes}
              onChange={(e: any) => setSelectedMarketsIncludes(e)}
              size="large"
              className="w-full"
              options={(() => {
                const fromLocation = locationData.map((ele: any) => ({
                  label: ele.locationOriginal,
                  value: ele.locationOriginal,
                }));
                // Always include pre-selected markets as options so they render
                // as tags even while locationData is still loading (edit mode).
                const locationSet = new Set(fromLocation.map((o: any) => o.value));
                const fromSelected = selectedMarketsIncludes
                  .filter(m => !locationSet.has(m))
                  .map(m => ({ label: m, value: m }));
                return [...fromLocation, ...fromSelected];
              })()}
              maxTagCount="responsive"
            />
            <p className="text-sm text-[var(--text-secondary)] mt-2">
              Select the markets you want to analyze individually
            </p>
          </div>
        )}

        {/* AI QC Toggle */}
        {["dev", "staging"].includes(process.env.REACT_APP_ENV ?? "dev") && (

          <div className="bg-[var(--bg-elevated)] rounded-xl p-6 shadow-sm border border-[var(--border-default)] hover:shadow-md transition-shadow">
            <label className="flex items-center gap-2 text-[1rem] font-semibold text-[var(--text-primary)] mb-4">
              <span className="w-8 h-8 bg-[var(--blue-subtle)] rounded-lg flex items-center justify-center text-[var(--blue)] font-bold text-sm">
                {(mode === "evaluate") ? "10" : "9"}
              </span>
              AI Quality Check
            </label>

            <div className="flex items-center justify-between bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-xl p-4">
              <div>
                <p className="font-semibold text-[var(--text-primary)]">
                  Enable AI QC Computation
                </p>
                <p className="text-xs text-[var(--text-secondary)] mt-1">
                  Run AI-based quality checks on input data
                </p>
              </div>

              <Checkbox
                checked={isAiQcEnabled}
                onChange={(e) => setIsAiQcEnabled(e.target.checked)}
                className="scale-125"
              />
            </div>

            {isAiQcEnabled && (
              <p className="text-sm text-[var(--blue)] mt-3 flex items-center gap-2 bg-[var(--blue-subtle)] p-3 rounded-lg">
                <span>ℹ️</span>
                AI QC is enabled.
              </p>
            )}
          </div>
        )}


        {/* Geographic Distribution — single stable container so the loading
            spinner and the map don't cause the page to jump as data arrives */}
        {(loadingLocations || cityDataForMap.length > 0 || US_STATE_DATA.length > 0) && (
          <div className="bg-[var(--bg-elevated)] rounded-xl p-6 shadow-lg border border-[var(--border-default)]" style={{ minHeight: 420, pointerEvents: "auto" }}>
            <div className="mb-4">
              <h3 className="text-xl font-bold text-[var(--text-primary)] flex items-center gap-2">
                <span className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white text-lg">
                  📍
                </span>
                Geographic Distribution
              </h3>
              <p className="text-sm text-[var(--text-secondary)] mt-2">
                Visual representation of your campaign markets and sales data
              </p>
            </div>

            {loadingLocations ? (
              <div className="flex flex-col items-center justify-center gap-4" style={{ minHeight: 340 }}>
                <div className="relative">
                  <div className="animate-spin rounded-full h-16 w-16 border-4 border-[var(--border-default)] border-t-[var(--blue)]"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <CustomExcelIcon className="text-[var(--blue)] text-2xl" />
                  </div>
                </div>
                <div className="text-center">
                  <p className="font-semibold text-[var(--text-primary)] text-lg">
                    Processing Locations
                  </p>
                  <p className="text-[var(--blue)] text-sm mt-1">
                    Fetching coordinates and preparing map data...
                  </p>
                </div>
              </div>
            ) : (
              <div
                className="w-full overflow-visible rounded-lg"
                style={{ touchAction: "auto" }}
              >
                <WorldUSSalesMap
                  stateData={US_STATE_DATA}
                  showLegend={true}
                  cityData={cityDataForMap}
                />
              </div>
            )}
          </div>
        )}

        {/* Database Data Selector Modal */}
        <DatabaseDataSelector
          isOpen={showDatabaseModal}
          onClose={() => setShowDatabaseModal(false)}
          onConfirm={handleLoadFromDatabase}
          isLoading={loadingDatabase}
        />

        {/* Column Mapping Modal */}
        {validationResult && (
          <ColumnMappingModal
            visible={showMappingModal}
            validationResult={validationResult}
            title="Map Your Data Columns"
            onConfirm={handleMappingConfirm}
            onCancel={handleMappingCancel}
            confirmText="Upload & Continue"
          />
        )}

        {/* <style jsx>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out;
          }
        `}</style> */}
      </div>
    );
  }
);

export default PreCampaign;
